import React, { useEffect } from "react";
import styled from "@emotion/styled";
import { useFormContext, useWatch } from "react-hook-form";
import LabelWithInput from "@components/input/LabelWithInput";
import NumberInput from "@components/input/NumberInput";

const Container = styled.div({
  display: "flex",
  flexDirection: "column",
  gap: "2rem",
});

const Section = styled.div({
  display: "flex",
  flexDirection: "column",
  gap: "1rem",
});

const Description = styled.div(({ theme }) => ({
  color: theme.color.gray,
  fontSize: "1.2rem",
  lineHeight: "1.6rem",
}));

const ErrorMessage = styled.div(({ theme }) => ({
  color: theme.color.orange,
  fontSize: "1.2rem",
}));

const InputRow = styled.div({
  display: "flex",
  gap: "1rem",
  alignItems: "center",
});

const TripleInputRow = styled.div({
  display: "grid",
  gridTemplateColumns: "repeat(3, 1fr) auto",
  gap: "1rem",
  alignItems: "center",
});

const InputWithUnit = styled.div({
  display: "flex",
  alignItems: "center",
  gap: "1rem",
});

const Unit = styled.span(({ theme }) => ({
  minWidth: "2rem",
  fontSize: "1.6rem",
  fontWeight: theme.fontWeight.bold,
  color: theme.color.black,
}));

const HelperCard = styled.div(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: "0.6rem",
  padding: "1.2rem",
  backgroundColor: theme.color.border_gray,
  border: `1px solid ${theme.color.light_gray}`,
  fontSize: "1.2rem",
  lineHeight: "1.6rem",
}));

const ShippingSpecInput = ({ specRef }: { specRef?: any }) => {
  const {
    trigger,
    control,
    formState: { errors },
  } = useFormContext();

  const watchedValues = useWatch({
    control,
    name: [
      "product_weight_kg",
      "shipping_weight_kg",
      "volume_width_cm",
      "volume_depth_cm",
      "volume_height_cm",
    ],
  });

  const [
    productWeightKg,
    shippingWeightKg,
    volumeWidthCm,
    volumeDepthCm,
    volumeHeightCm,
  ] = watchedValues || [];

  useEffect(() => {
    const names = [
      "product_weight_kg",
      "shipping_weight_kg",
      "volume_width_cm",
      "volume_depth_cm",
      "volume_height_cm",
    ] as const;

    const values = [
      productWeightKg,
      shippingWeightKg,
      volumeWidthCm,
      volumeDepthCm,
      volumeHeightCm,
    ];

    values.forEach((value, index) => {
      if (value !== null && value !== undefined && value !== "") {
        trigger(names[index]);
      }
    });
  }, [
    trigger,
    productWeightKg,
    shippingWeightKg,
    volumeWidthCm,
    volumeDepthCm,
    volumeHeightCm,
  ]);

  return (
    <Container ref={specRef}>
      <Section>
        <LabelWithInput label="상품무게" required>
          {errors.product_weight_kg && (
            <ErrorMessage>{errors.product_weight_kg.message?.toString()}</ErrorMessage>
          )}
          <Description>
            포장재를 제외한 상품 자체의 무게를 입력합니다. 입력하신 상품무게를
            배송비 자동책정의 요인으로 사용합니다.
          </Description>
          <InputRow>
            <InputWithUnit>
              <NumberInput
                name="product_weight_kg"
                mode="decimal"
                dir="rtl"
                placeholder="0.00~100.00"
              />
              <Unit>kg</Unit>
            </InputWithUnit>
          </InputRow>
        </LabelWithInput>
      </Section>

      <Section>
        <LabelWithInput label="배송무게" required>
          {errors.shipping_weight_kg && (
            <ErrorMessage>{errors.shipping_weight_kg.message?.toString()}</ErrorMessage>
          )}
          <Description>
            실제 배송을 기준으로 포장 완료 무게를 입력합니다. 입력하신 배송무게를
            배송비 자동책정의 요인으로 사용합니다.
          </Description>
          <InputRow>
            <InputWithUnit>
              <NumberInput
                name="shipping_weight_kg"
                mode="decimal"
                dir="rtl"
                placeholder="0.00~100.00"
              />
              <Unit>kg</Unit>
            </InputWithUnit>
          </InputRow>
        </LabelWithInput>
      </Section>

      <Section>
        <LabelWithInput label="부피" required>
          {(errors.volume_width_cm ||
            errors.volume_depth_cm ||
            errors.volume_height_cm) && (
            <ErrorMessage>
              {errors.volume_width_cm?.message?.toString() ||
                errors.volume_depth_cm?.message?.toString() ||
                errors.volume_height_cm?.message?.toString()}
            </ErrorMessage>
          )}
          <Description>
            포장 박스의 외부 사이즈를 입력합니다. 입력하신 부피를 배송비 자동책정의
            요인으로 사용합니다.
          </Description>
          <TripleInputRow>
            <NumberInput
              name="volume_width_cm"
              mode="decimal"
              dir="rtl"
              placeholder="가로"
            />
            <NumberInput
              name="volume_depth_cm"
              mode="decimal"
              dir="rtl"
              placeholder="세로"
            />
            <NumberInput
              name="volume_height_cm"
              mode="decimal"
              dir="rtl"
              placeholder="높이"
            />
            <Unit>cm</Unit>
          </TripleInputRow>
        </LabelWithInput>
      </Section>

      <HelperCard>
        <span>* 배송 스펙 입력 안내</span>
        <span>입력한 무게와 부피 정보는 자동 배송비 산정에 사용됩니다.</span>
      </HelperCard>
    </Container>
  );
};

export default ShippingSpecInput;
